import { lazy } from 'react'
import { Navigate } from 'react-router'

import { ErrorPageNotFound } from '../components/ErrorPage'
import {
  ROUTE_PATH_LIST_SETTINGS,
  ROUTE_UI_SECTION_NAME,
} from '../constants/route'
import { isCalendarSettingsAvailable } from '../utils/calendar'
import {
  getPermissionGuardLoader,
  getPermissionProtectedRouteLoader,
  getSettingsPageGuardLoader,
} from '../utils/route'

const CalendarSettings = lazy(() =>
  import('../components/CalendarSettings').then(m => ({
    default: m.CalendarSettings,
  }))
)

const CompanyEpostBilling = lazy(() =>
  import('../components/EpostBillingSettings').then(m => ({
    default: m.EpostBillingSettings,
  }))
)
const ColorThemeSettings = lazy(() =>
  import('../components/CompanySettings').then(m => ({
    default: m.ColorThemeSettings,
  }))
)
const CompanySettings = lazy(() =>
  import('../components/CompanySettings').then(m => ({
    default: m.CompanySettings,
  }))
)

const ConfigurationSettings = lazy(() =>
  import('../components/ConfigurationSettings').then(m => ({
    default: m.ConfigurationSettings,
  }))
)

const ERPIntegrationSettings = lazy(() =>
  import('../components/ERPIntegrationSettings').then(m => ({
    default: m.ERPIntegrationSettings,
  }))
)

const GoogleDriveIntegrationSettings = lazy(() =>
  import('../components/GoogleDriveIntegrationSettings').then(m => ({
    default: m.GoogleDriveIntegrationSettings,
  }))
)

const Labels = lazy(() =>
  import('../components/Labels').then(m => ({
    default: m.Labels,
  }))
)

const ManageAuthorizationsSettings = lazy(() =>
  import('../components/ManageAuthorizationsSettings').then(m => ({
    default: m.ManageAuthorizationsSettings,
  }))
)

const PersonalSettings = lazy(() =>
  import('../components/PersonalSettings').then(m => ({
    default: m.PersonalSettings,
  }))
)

const TariffsLimits = lazy(() =>
  import('../components/TariffsLimits').then(m => ({
    default: m.TariffsLimits,
  }))
)

export function getSettingsRoutes({ availableSections, sections }) {
  return [
    {
      children: [
        {
          element: <Navigate replace to={ROUTE_PATH_LIST_SETTINGS.company} />,
          index: true,
        },
        {
          element: <PersonalSettings />,
          loader: getSettingsPageGuardLoader({
            availableSections,
            path: ROUTE_PATH_LIST_SETTINGS.personal,
            sectionName: ROUTE_UI_SECTION_NAME.settingsPersonal,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.personal,
        },
        {
          element: <CompanySettings />,
          loader: getSettingsPageGuardLoader({
            availableSections,
            path: ROUTE_PATH_LIST_SETTINGS.company,
            sectionName: ROUTE_UI_SECTION_NAME.settingsCompany,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.company,
        },
        {
          element: <GoogleDriveIntegrationSettings />,
          loader: getSettingsPageGuardLoader({
            availableSections,
            path: ROUTE_PATH_LIST_SETTINGS.googleDrive,
            sectionName: ROUTE_UI_SECTION_NAME.settingsGoogleDrive,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.googleDrive,
        },
        {
          element: <ERPIntegrationSettings />,
          loader: getSettingsPageGuardLoader({
            availableSections,
            path: ROUTE_PATH_LIST_SETTINGS.erpIntegration,
            sectionName: ROUTE_UI_SECTION_NAME.settingsErpIntegration,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.erpIntegration,
        },
        {
          element: <ConfigurationSettings />,
          loader: getSettingsPageGuardLoader({
            availableSections,
            path: ROUTE_PATH_LIST_SETTINGS.configuration,
            sectionName: ROUTE_UI_SECTION_NAME.settingsConfiguration,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.configuration,
        },
        {
          element: <ManageAuthorizationsSettings />,
          loader: getSettingsPageGuardLoader({
            availableSections,
            path: ROUTE_PATH_LIST_SETTINGS.manageAuth,
            sectionName: ROUTE_UI_SECTION_NAME.settingsManageAuth,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.manageAuth,
        },
        {
          element: <ColorThemeSettings />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME.settingsColorTheme,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.colorTheme,
        },
        {
          element: <Labels />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME.settingsLabels,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.labels,
        },
        {
          element: <CompanyEpostBilling />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME.settingsEpostBilling,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.epostBilling,
        },
        {
          element: <TariffsLimits />,
          loader: getPermissionGuardLoader({
            sectionName: ROUTE_UI_SECTION_NAME.settingsTariffsLimits,
            sections,
          }),
          path: ROUTE_PATH_LIST_SETTINGS.tariffs,
        },
        {
          element: <CalendarSettings />,
          loader: () =>
            getPermissionProtectedRouteLoader({
              hasAccess: isCalendarSettingsAvailable({
                availableSections,
                sections,
              }),
            }),
          path: ROUTE_PATH_LIST_SETTINGS.calendar,
        },
      ],
      errorElement: <ErrorPageNotFound />,
    },
  ]
}
